import { red } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';

import { ParagonColors } from '@paragon/client-tools';

declare module '@mui/material/styles' {
  interface Palette {
    selectedList: {
      main: string;
    };
    canvas: {
      main: string;
    };
    border: {
      main: string;
    };
  }

  interface PaletteOptions {
    selectedList: {
      main: string;
    };
    canvas?: {
      main: string;
    };
    border?: {
      main: string;
    };
  }
}

export const darkTheme = createTheme({
  typography: {
    fontFamily: 'Roboto Condensed, Figtree, sans-serif',
    // Figtree Semi-Bold for the App Header/Pargon name
    h1: {
      fontSize: '1.2rem',
      fontFamily: 'Figtree',
      fontWeight: 600,
      webkitFontSmoothing: 'antialiased',
    },
    // Figtree Regular for Primary Headings
    h2: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h3: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h4: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h5: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h6: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Regular for Standard Headings and Actions
    subtitle1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    subtitle2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    button: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    overline: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Light for captions
    caption: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 300,
      webkitFontSmoothing: 'antialiased',
    },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          borderColor: '#ffffff',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: 'transparent',
            //This is the primary color
            borderColor: ParagonColors.green,
          },
        },
      },
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: ParagonColors.green,
    },
    secondary: {
      main: ParagonColors.paragold,
    },
    error: {
      main: red[900],
    },
    canvas: {
      main: ParagonColors.canvasDark,
    },
    // This may change depending on the theme
    selectedList: {
      main: ParagonColors.green,
    },
    border: {
      main: '#c3c3c3',
    },
    background: {
      default: ParagonColors.background,
      paper: ParagonColors.background,
    },
  },
});

export const lightTheme = createTheme({
  typography: {
    fontFamily: 'Roboto Condensed, Figtree, sans-serif',
    // Figtree Semi-Bold for the App Header/Pargon name
    h1: {
      fontSize: '1.2rem',
      fontFamily: 'Figtree',
      fontWeight: 600,
      webkitFontSmoothing: 'antialiased',
    },
    // Figtree Regular for Primary Headings
    h2: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h3: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h4: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h5: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h6: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Regular for Standard Headings and Actions
    subtitle1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    subtitle2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    button: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    overline: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Light for captions
    caption: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 300,
      webkitFontSmoothing: 'antialiased',
    },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
        },
      },
    },
  },
  palette: {
    primary: {
      main: ParagonColors.green,
    },
    secondary: {
      main: ParagonColors.paragold,
    },
    error: {
      main: red[900],
    },
    canvas: {
      main: ParagonColors.canvasLight,
    },
    selectedList: {
      main: ParagonColors.green,
    },
    border: {
      main: '#c3c3c3',
    },
  },
});
